// Page: ManageVideoTemplates
// Purpose: Admin list and filters for video templates. Supports search,
// format tags, sorting, pagination, preview, edit, and delete.
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { useAuth } from '../../../context/auth';
import { useToast } from '../../../components/ui/notifications/toast-notification.jsx';
import { ConfirmationModal } from '../../../components/ui/modals/confirmation-modal.jsx';
import { VideoTemplateModal } from '../../../components/admin/videoTemplates/VideoTemplateModal';
import Footer from '../../../components/shared/Footer';
import { Video, Search, X, Filter, ArrowUpDown, ChevronDown, ArrowLeft, Edit, Trash2, Play, ExternalLink } from 'lucide-react';
import { VIDEO_TEMPLATE_FORMAT_LIST } from '../../../data/tags/tagEnums';
import { getAllVideoTemplatesForAdmin, deleteVideoTemplate } from '../../../api/content/videoTemplatesApi';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function ManageVideoTemplates() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is the admin
  const isAdmin = isUserAdmin(user);

  // State for video templates management
  const [videoTemplates, setVideoTemplates] = useState([]);
  const [filteredVideoTemplates, setFilteredVideoTemplates] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingVideoTemplates, setLoadingVideoTemplates] = useState(false);

  // Filter and search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Delete modal state
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    videoTemplateId: null,
    title: '',
    isDeleting: false
  });

  // Video modal state
  const [videoModal, setVideoModal] = useState({
    isOpen: false,
    videoTemplate: null
  });

  // Refs for dropdowns
  const sortDropdownRef = useRef(null);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // Load video templates
  const loadVideoTemplates = async (page = 1, resetList = false) => {
    if (loadingVideoTemplates) return;
    
    setLoadingVideoTemplates(true);
    
    try {
      const options = {
        page,
        limit: 25,
        search: searchQuery,
        tags: selectedTags.join(','),
        sortBy
      };

      const response = await getAllVideoTemplatesForAdmin(options);
      
      if (response) {
        const newVideoTemplates = resetList ? response.videoTemplates : [...videoTemplates, ...response.videoTemplates];
        
        setVideoTemplates(newVideoTemplates);
        setFilteredVideoTemplates(newVideoTemplates);
        setTotalCount(response.totalCount || 0);
        setCurrentPage(response.currentPage || page);
        setTotalPages(response.totalPages || 1);
        setHasMore(response.hasMore || false);
      }
    } catch (error) {
      console.error('Error loading video templates:', error);
      addToast('Failed to load video templates. Please try again.', 'error');
    } finally {
      setLoadingVideoTemplates(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (!isLoading && isAdmin) {
      loadVideoTemplates(1, true);
    }
  }, [isLoading, isAdmin, searchQuery, selectedTags, sortBy]);

  // Handle search
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  // Handle tag filter
  const handleTagToggle = (tag) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag];
      setCurrentPage(1);
      return newTags;
    });
  };

  // Handle sort change
  const handleSortChange = (newSortBy) => {
    setSortBy(newSortBy);
    setCurrentPage(1);
    setShowSortDropdown(false);
  };

  // Handle load more
  const handleLoadMore = () => {
    if (hasMore && !loadingVideoTemplates) {
      loadVideoTemplates(currentPage + 1, false);
    }
  };

  // Handle delete video template
  const handleDeleteVideoTemplate = (videoTemplateId, title) => {
    setDeleteModal({
      isOpen: true,
      videoTemplateId,
      title,
      isDeleting: false
    });
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteModal.videoTemplateId) return;

    setDeleteModal(prev => ({ ...prev, isDeleting: true }));

    try {
      await deleteVideoTemplate(deleteModal.videoTemplateId);
      
      addToast('The video template has been deleted successfully.', 'success');

      // Close modal and refresh the list
      setDeleteModal({ isOpen: false, videoTemplateId: null, title: '', isDeleting: false });
      loadVideoTemplates(1, true);
    } catch (error) {
      console.error('Error deleting video template:', error);
      addToast('Failed to delete the video template. Please try again.', 'error');
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, videoTemplateId: null, title: '', isDeleting: false });
  };

  // Handle video preview
  const handlePreviewVideo = (videoTemplate) => {
    setVideoModal({
      isOpen: true,
      videoTemplate: videoTemplate
    });
  };

  const closeVideoModal = () => {
    setVideoModal({
      isOpen: false,
      videoTemplate: null
    });
  };

  // Handle edit
  const handleEditVideoTemplate = (videoTemplateId) => {
    navigate(`/app/private/editVideoTemplate/${videoTemplateId}`);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sortDropdownRef]);

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading video templates management..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  const sortOptions = [
    { value: 'newest', label: 'Newest first' },
    { value: 'oldest', label: 'Oldest first' },
    { value: 'title-asc', label: 'Title A-Z' },
    { value: 'title-desc', label: 'Title Z-A' }
  ];

  return (
    <div className="p-6 w-full max-w-[1600px] mx-auto">
      {/* Back to Admin Dashboard button */}
      <div className="mb-4">
        <button
          onClick={() => navigate('/app/private/admin/templates')}
          className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Templates
        </button>
      </div>

      {/* Header */}
      <div className="mb-6 flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Manage Video Templates</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            View, edit, and manage all video templates in the system.
          </p>
        </div>
        <button
          onClick={() => navigate('/app/private/createVideoTemplate')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <Video className="h-4 w-4" />
          Create Video Template
        </button>
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="bg-white dark:bg-gray-900 overflow-hidden shadow rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Video className="h-8 w-8 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                    Total Video Templates
                  </dt>
                  <dd className="text-lg font-medium text-gray-900 dark:text-white">
                    {totalCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 space-y-4">
        {/* Search and Filter Toggle */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search video templates..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                showFilters || selectedTags.length > 0
                  ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {selectedTags.length > 0 && (
                <span className="bg-white bg-opacity-20 text-xs px-1.5 py-0.5 rounded-full">
                  {selectedTags.length}
                </span>
              )}
            </button>

            {/* Sort Dropdown */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <ArrowUpDown className="h-4 w-4" />
                Sort
                <ChevronDown className="h-4 w-4" />
              </button>
              
              {showSortDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                  <div className="py-1">
                    {sortOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleSortChange(option.value)}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                          sortBy === option.value
                            ? 'bg-primary-50 text-primary-600 dark:bg-primary-900 dark:text-primary-300'
                            : 'text-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Format Tags Filter */}
        {showFilters && (
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Video Formats</h3>
              <div className="flex flex-wrap gap-2">
                {VIDEO_TEMPLATE_FORMAT_LIST.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => handleTagToggle(tag)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors duration-200 ${
                      selectedTags.includes(tag)
                        ? 'bg-primary-600 text-white border-primary-600 hover:bg-primary-700'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            
            {selectedTags.length > 0 && (
              <div className="flex justify-end">
                <button
                  onClick={() => setSelectedTags([])}
                  className="text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  Clear all filters
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Video Templates Grid */}
      <div className="bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm">
        {loadingVideoTemplates && currentPage === 1 ? (
          <div className="flex items-center justify-center p-12">
            <LoadingSpinner message="Loading video templates..." />
          </div>
        ) : filteredVideoTemplates.length === 0 ? (
          <div className="text-center py-12">
            <Video className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No video templates found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchQuery || selectedTags.length > 0
                ? 'Try adjusting your search or filter criteria.'
                : 'Get started by creating your first video template.'}
            </p>
            {(!searchQuery && selectedTags.length === 0) && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/app/private/createVideoTemplate')}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <Video className="h-4 w-4 mr-2" />
                  Create Video Template
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredVideoTemplates.map((template) => (
              <div
                key={template.id}
                className="bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:bg-white/5 dark:hover:bg-gray-800/20 transition-all"
              >
                <div className="flex items-start justify-between">
                  {/* Left side - Thumbnail and content */}
                  <div className="flex items-start space-x-4 flex-1 min-w-0">
                    {/* Thumbnail */}
                    <div className="flex-shrink-0">
                      {template.thumbnailUrl ? (
                        <div 
                          className="relative cursor-pointer group"
                          onClick={() => handlePreviewVideo(template)}
                        >
                          <img
                            src={template.thumbnailUrl}
                            alt={template.title}
                            className="h-20 w-32 object-cover rounded-lg border border-gray-200 dark:border-gray-600 transition-opacity group-hover:opacity-75"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-black/60 rounded-full p-2">
                              <Play className="h-6 w-6 text-white" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div 
                          className="h-20 w-32 bg-gray-100 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          onClick={() => handlePreviewVideo(template)}
                        >
                          <Video className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3">
                        <Video className="h-5 w-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white truncate">
                          {template.title}
                        </h3>
                      </div>
                      
                      <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                        <span>Created: {new Date(template.createdAt).toLocaleDateString()}</span>
                        {template.canvaLink && (
                          <>
                            <span>•</span>
                            <a
                              href={template.canvaLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                            >
                              <ExternalLink className="w-3 h-3 mr-1" />
                              Edit in Canva
                            </a>
                          </>
                        )}
                      </div>

                      {template.tags && template.tags.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {template.tags.slice(0, 5).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                            >
                              {tag}
                            </span>
                          ))}
                          {template.tags.length > 5 && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200">
                              +{template.tags.length - 5} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right side - Action buttons */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEditVideoTemplate(template.id)}
                      className="inline-flex items-center p-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      title="Edit Video Template"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteVideoTemplate(template.id, template.title)}
                      className="inline-flex items-center p-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      title="Delete Video Template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center pt-6">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingVideoTemplates}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {loadingVideoTemplates ? (
                    <>
                      <LoadingSpinner size="sm" />
                      <span className="ml-2">Loading...</span>
                    </>
                  ) : (
                    'Load More'
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Video Template"
        message={`Are you sure you want to delete "${deleteModal.title}"? This action cannot be undone.`}
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
        isLoading={deleteModal.isDeleting}
      />

      {/* Video Preview Modal */}
      <VideoTemplateModal
        isOpen={videoModal.isOpen}
        onClose={closeVideoModal}
        videoTemplate={videoModal.videoTemplate}
      />

      <Footer />
    </div>
  );
} 